import { schemas, useClient, useQuery } from '@weenat/client'
import { AlertCreationType } from '@weenat/client/dist/core/alerts/alerts'
import { useIntl } from '@weenat/wintl'
import SuperForm from 'app/src/kit/SuperForm'
import CheckboxField from 'app/src/kit/fields/CheckboxField'
import UseOnChange from 'app/src/kit/fields/UseOnChange'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import noop from 'lodash-es/noop'
import { useMemo, useState } from 'react'
import useDisclosure from '../hooks/useDisclosure'
import Button from '../kit/Button'
import Card from '../kit/Card'
import InfoCard from '../kit/InfoCard'
import Modal from '../kit/Modal'
import Text from '../kit/Text'
import EditMyPhoneNumbers from '../me/EditMyPhoneNumbers'
import AlertCreationStep, { AlertCreationStepProps } from './AlertCreationStep'
import SaveWeenatNumberTipCard from './SaveWeenatNumberTipCard'

type StepResult = {
  isVoiceCallEnabled: boolean
  telephoneNumber?: string
  telephoneCountryCode?: string
}

type NotificationMethodStepProps = AlertCreationStepProps<StepResult>

type PickAlertNotificationMethodProps = NotificationMethodStepProps & {
  isVoiceCallEnabled: boolean
  currentAlertCreationType: AlertCreationType
}

export const AlertPhoneNumber: FC<{ viewOnly?: boolean }> = ({ viewOnly = false }) => {
  const { t } = useIntl()
  const modifyNumbersModal = useDisclosure()

  const client = useClient()

  const allPhoneNumbersRequest = useQuery(client.me.phoneNumbers.getAll())
  const allPhones = allPhoneNumbersRequest.data ?? []
  return (
    <>
      <InfoCard type='info' title={t('user_settings.phone_numbers.your_phone_numbers')}>
        <Text>{t('user_settings.phone_numbers.alert_explanations')}</Text>
        <ul>
          {allPhones.map((phone) => (
            <li key={phone.number} style={{ listStyle: 'initial', listStylePosition: 'inside' }}>
              {phone.number}
            </li>
          ))}
        </ul>
        {!viewOnly ? (
          <Button
            backgroundColor='grayscale.black'
            color='grayscale.white'
            variant='compact'
            style={{ alignSelf: 'flex-end' }}
            onPress={modifyNumbersModal.open}
          >
            {t('user_settings.phone_numbers.modify')}
          </Button>
        ) : null}
      </InfoCard>
      <Modal
        title={t('models.phoneNumber.model.name', { capitalize: true })}
        isOpen={modifyNumbersModal.isOpen}
        close={modifyNumbersModal.close}
        $noInnerPadding
      >
        <EditMyPhoneNumbers insideModal onCancel={modifyNumbersModal.close} />
      </Modal>
    </>
  )
}

const PickAlertNotificationMethod: FC<PickAlertNotificationMethodProps> = ({
  isVoiceCallEnabled: initialVoiceCallEnabled,
  onPressNext = noop,
  currentAlertCreationType,
  ...props
}) => {
  const { t } = useIntl()
  const client = useClient()

  const allPhoneNumbersRequest = useQuery(client.me.phoneNumbers.getAll())
  const allPhones = allPhoneNumbersRequest.data ?? []

  const primaryNumber = allPhones.find((phone) => phone.isPrimary)

  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [stepResult, setStepResult] = useState<StepResult>({
    isVoiceCallEnabled: initialVoiceCallEnabled
  })

  const initialValues = useMemo(
    () => ({
      isVoiceCallEnabled: initialVoiceCallEnabled,
      telephoneNumber: !isNil(primaryNumber) ? primaryNumber.number : '',
      telephoneCountryCode: !isNil(primaryNumber) ? primaryNumber.countryCode : 'FR'
    }),
    [primaryNumber, initialVoiceCallEnabled]
  )

  return (
    <SuperForm
      schema={schemas.alertCreation.notificationMethod}
      initialValues={initialValues}
      onSubmit={noop}
    >
      {({ errors, values, isValid }) => (
        <>
          <AlertCreationStep
            {...props}
            canProgressToNext={isFormValid}
            onPressNext={() => onPressNext(stepResult)}
          >
            <UseOnChange
              onChange={({
                isVoiceCallEnabled,
                telephoneNumber,
                telephoneCountryCode
              }: StepResult) => {
                let isReallyValid = false

                if (isVoiceCallEnabled) {
                  isReallyValid =
                    isNil(errors.telephoneNumber) &&
                    !isEmpty(telephoneNumber) &&
                    isNil(errors.telephoneCountryCode) &&
                    !isEmpty(telephoneCountryCode)
                } else {
                  isReallyValid = true
                }

                setIsFormValid(isValid && isReallyValid)
                setStepResult({
                  isVoiceCallEnabled,
                  telephoneNumber: !isEmpty(telephoneNumber) ? telephoneNumber : undefined,
                  telephoneCountryCode: !isEmpty(telephoneNumber) ? telephoneCountryCode : undefined
                })
              }}
            />
            <Card $mb='md'>
              <CheckboxField
                name={'isVoiceCallEnabled'}
                label={t('alerts.paramSetups.fieldLabels.enable_voice_call')}
                $hideErrors
              />
            </Card>
            <>
              {!values.isVoiceCallEnabled ? null : (
                <Flex $flexDirection='column' $gap='md'>
                  <AlertPhoneNumber />
                  <SaveWeenatNumberTipCard />
                </Flex>
              )}
            </>
          </AlertCreationStep>
        </>
      )}
    </SuperForm>
  )
}

export default PickAlertNotificationMethod
